import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { Icon } from '@iconify/react';
import { Input, Chip, RadioGroup, Radio, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Checkbox } from '@nextui-org/react';
import { useGetAiworkersPresignedUrlMutation, useUploadFileToS3Mutation } from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast } from '../toast/CtyToast';
import {
  formatCurrency,
  formatCustomerName,
  formatDate,
  sanitizeVinInput,
  buildContractLookupQueryParameters,
  LabeledHorizontalRadios,
  type ContractData,
  type ContractLookupQueryBuildResult,
} from './claimIntakeUtils';
import {
  NOT_THE_SERVICER_DISCLAIMER,
  VehicleNotAtCenterModal,
} from './VehicleNotAtCenterModal';
import {
  hasBlockingPendingClaims,
  pendingClaimBlockMessage,
  runClaimIntakeOperation,
  type ClaimIntakeSession,
} from './claimIntakeAgentApi';
import { uploadClaimIntakeFilesViaPresigned } from './claimIntakeRum';
import {
  applyParserResultsToFormState,
  extractContractNumberFromParserResults,
  extractVinFromParserResults,
  firstScalar,
  readParserResultsFromDocument,
  type ClaimIntakeParserSetters,
} from './claimIntakeParser';
import { useTranslation } from 'react-i18next';
import { ContractSurchargesSection } from './claimIntakeReadOnlySections';
import {
  presentClaimIntakeError,
  type ClaimSubmitErrorModalContent,
} from './claimSubmitErrorUtils';
import { ClaimIntakeFileUploadCard } from './ClaimIntakeFileUploadCard';
import {
  buildAdditionalSourcesFromGenerateResponse,
  removeAdditionalSourcesForFile,
  type AdditionalSourceItem,
} from './additionalInformationUpload';

const UPLOAD_LOADING_MESSAGES = [
  'Uploading the file…',
  'Reading the file…',
  'Processing the information…',
  'Reading repair data…',
  'Mapping repair data…',
  'Taking longer than expected…',
] as const;

const UPLOAD_MESSAGE_INTERVAL_MS = 10000;

const VIN_INPUT_LABEL = 'VIN Number';

function contractDetailRows(contract: ContractData) {
  return [
    { key: 'cn', label: 'Contract Number', text: contract.contractNumber },
    { key: 'holder', label: 'Contract Holder', text: formatCustomerName(contract.customer) },
    { key: 'cov', label: 'Coverage Plan', text: contract.coverage?.description || '—' },
    // { key: 'eff', label: 'Effective Date', text: formatDate(contract.effectiveDate) },
    // { key: 'exp', label: 'Expiration Date', text: formatDate(contract.expirationDate) },
    { key: 'vin', label: 'VIN', text: contract.vehicle?.vin },
    {
      key: 'pt',
      label: 'Product Type',
      text: contract.coverage?.productType?.code || contract.coverage?.productType?.description || '—',
    },
  ];
}

function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts[parts.length - 1];
}

function getParsedDocumentFileName(parsedClaimDocument: Record<string, unknown> | null): string | null {
  if (!parsedClaimDocument) return null;
  if (typeof parsedClaimDocument.file_name === 'string' && parsedClaimDocument.file_name) {
    return parsedClaimDocument.file_name;
  }
  if (typeof parsedClaimDocument.filename === 'string' && parsedClaimDocument.filename) {
    return parsedClaimDocument.filename;
  }
  return null;
}


function isSameUploadedFile(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

function buildEstimateParserSources(
  files: readonly File[],
  urlList: readonly { source_id?: string }[]
): Array<{ source_id: string; source_extension: string; file_name: string }> {
  return files.flatMap((file, index) => {
    const sourceId = urlList[index]?.source_id;
    if (!sourceId) return [];
    return [
      {
        source_id: sourceId,
        source_extension: getFileExtension(file.name),
        file_name: file.name,
      },
    ];
  });
}

/** Returns every contract from the lookup workflow response (merges `contracts` across `data` rows when present). */
function extractContractsFromLookupResult(result: Record<string, unknown>): ContractData[] | undefined {
  const fromRoot = (result as { contracts?: ContractData[] })?.contracts;
  const data = result?.data;
  if (!Array.isArray(data) || data.length === 0) {
    return fromRoot;
  }

  const merged: ContractData[] = [];
  for (const row of data) {
    if (row == null || typeof row !== 'object' || Array.isArray(row)) continue;
    const contracts = (row as Record<string, unknown>).contracts;
    if (!Array.isArray(contracts)) continue;
    for (const item of contracts) {
      if (item != null && typeof item === 'object' && !Array.isArray(item)) {
        merged.push(item as ContractData);
      }
    }
  }
  if (merged.length > 0) return merged;

  const firstRow = data[0] as Record<string, unknown> | undefined;
  return (firstRow?.contracts as ContractData[] | undefined) ?? fromRoot;
}

/** Pulls the first parsed-document row + its `parser_results` from the parser workflow response. */
function extractParsedDocumentAndResults(parserResponse: Record<string, unknown>): {
  docItem: Record<string, unknown> | null;
  parserResults: Record<string, unknown> | null;
} {
  const payload = parserResponse?.data;
  const candidate = Array.isArray(payload) ? payload[0] : undefined;
  if (candidate == null || typeof candidate !== 'object') {
    return { docItem: null, parserResults: null };
  }
  const docItem = candidate as Record<string, unknown>;
  const results = docItem.parser_results;
  return {
    docItem,
    parserResults: results != null && typeof results === 'object' && !Array.isArray(results)
      ? (results as Record<string, unknown>)
      : null,
  };
}

function toLookupDetailError(message: string): ClaimSubmitErrorModalContent {
  return { summary: message, details: [] };
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/** One selectable file when the parser reports different VINs across the uploaded files. */
type MultiVinOption = Readonly<{
  fileName: string;
  originalFileName: string;
  vin: string;
  docItem: Record<string, unknown>;
  parserResults: Record<string, unknown> | null;
}>;

/** The parser's raw message plus one selectable option per file. */
type MultiVinConflict = Readonly<{
  message: string;
  options: MultiVinOption[];
}>;

/** Locates the estimateparser 400 payload (`{ status_code, message, data[] }`) inside a thrown error. */
function findMultiVinPayload(err: unknown): Record<string, unknown> | null {
  const queue: unknown[] = [err];
  const visited = new Set<unknown>();
  while (queue.length > 0) {
    const current = queue.shift();
    if (!isPlainRecord(current) || visited.has(current)) continue;
    visited.add(current);
    const statusCode = Number(current.status_code ?? current.statusCode);
    if (statusCode === 400 && Array.isArray(current.data)) return current;
    queue.push(current.data, current.error, current.text, current.outputs);
  }
  return null;
}

/**
 * Detects the estimateparser "different VINs detected" response and returns one option per file.
 * Returns null for every other failure so normal error handling stays intact.
 */
function extractMultiVinConflict(err: unknown): MultiVinConflict | null {
  const payload = findMultiVinPayload(err);
  if (!payload) return null;

  const message = typeof payload.message === 'string' ? payload.message : '';
  if (!/vins/i.test(message) || !/detect/i.test(message) || !/different/i.test(message)) return null;

  const data = payload.data;
  if (!Array.isArray(data)) return null;

  const options = data.flatMap((item): MultiVinOption[] => {
    if (!isPlainRecord(item)) return [];
    const parserResults = isPlainRecord(item.parser_results) ? item.parser_results : null;
    const vin =
      extractVinFromParserResults(parserResults) || firstScalar(item.Vehicle_Identification_Number).trim();
    const fileName = firstScalar(item.file_name).trim();
    const originalFileName = firstScalar(item.original_file_name).trim();
    if (!vin) return [];
    return [{ fileName, originalFileName, vin, docItem: item, parserResults }];
  });

  return options.length < 2 ? null : { message, options };
}

function ClaimIntakeDetailErrors({
  content,
}: Readonly<{ content: ClaimSubmitErrorModalContent | null }>) {
  if (!content) return null;

  const details = content.details ?? [];
  if (details.length === 0) {
    const summary = content.summary?.trim();
    if (!summary) return null;
    return (
      <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/40 rounded-lg text-sm text-red-600 dark:text-red-400">{summary}</div>
    );
  }

  return (
    <div className="space-y-2">
      <ul className="m-0 list-none space-y-1.5 p-0">
        {details.map((detail, index) => (
          <li key={`${detail.code}-${index}`} className="text-sm text-red-600 dark:text-red-400">
            {detail.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

function getEstimateParserJsonPayload(
  parsedClaimDocument: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (!parsedClaimDocument) return null;
  return readParserResultsFromDocument(parsedClaimDocument) ?? parsedClaimDocument;
}

function EstimateParserJsonModal({
  isOpen,
  onClose,
  parsedClaimDocument,
}: Readonly<{
  isOpen: boolean;
  onClose: () => void;
  parsedClaimDocument: Record<string, unknown> | null;
}>) {
  const jsonPayload = getEstimateParserJsonPayload(parsedClaimDocument);
  const jsonText = jsonPayload ? JSON.stringify(jsonPayload, null, 2) : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      placement="center"
      scrollBehavior="inside"
      classNames={{ backdrop: 'bg-black/50' }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 border-b border-default-200">
          <span className="text-base font-semibold text-foreground">Estimate Parser Response</span>
          <span className="text-sm font-normal text-default-500">Raw JSON returned from the estimate parser.</span>
        </ModalHeader>
        <ModalBody className="py-4">
          <pre className="max-h-[min(70vh,560px)] overflow-auto rounded-lg border border-default-200 bg-default-100 p-4 text-xs font-mono leading-relaxed text-default-700 whitespace-pre-wrap break-words">
            {jsonText}
          </pre>
        </ModalBody>
        <ModalFooter className="border-t border-default-200">
          <CtyButton color="primary" variant="flat" onPress={onClose}>
            Close
          </CtyButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

type DocumentUploadCardProps = Readonly<{
  isUploading: boolean;
  isDisabled?: boolean;
  uploadLoadingMessage: string;
  uploadError: string | null;
  uploadDetailError: ClaimSubmitErrorModalContent | null;
  uploadedFiles: readonly File[];
  parsedClaimDocument: Record<string, unknown> | null;
  onShowEstimateJson?: () => void;
  onFileDrop: (e: React.DragEvent<HTMLButtonElement>) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPickFile: () => void;
  onRemoveFile: (index: number) => void;
  onViewPdf?: (file: File) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  footerExtra?: React.ReactNode;
}>;

function DocumentUploadCard({
  isUploading,
  isDisabled = false,
  uploadLoadingMessage,
  uploadError,
  uploadDetailError,
  uploadedFiles,
  parsedClaimDocument,
  onShowEstimateJson,
  onFileDrop,
  onFileSelect,
  onPickFile,
  onRemoveFile,
  onViewPdf,
  fileInputRef,
  footerExtra,
}: DocumentUploadCardProps) {
  const hasEstimateJson = getEstimateParserJsonPayload(parsedClaimDocument) != null;
  return (
    <ClaimIntakeFileUploadCard
      title="Document Upload"
      isUploading={isUploading}
      isDisabled={isDisabled}
      loadingMessage={uploadLoadingMessage}
      idleTitle="Click to drop a repair estimate, invoice, image, or video"
      idleHint="PDF, JPG, PNG, MP4 — multiple files supported"
      accept=".pdf,.jpg,.jpeg,.png,.mp4"
      uploadError={uploadError}
      detailError={uploadDetailError ? <ClaimIntakeDetailErrors content={uploadDetailError} /> : null}
      uploadedFiles={uploadedFiles}
      headerExtra={
        hasEstimateJson && onShowEstimateJson ? (
          <CtyButton
            size="sm"
            variant="flat"
            color="default"
            className="ml-auto font-medium"
            startContent={<Icon icon="mdi:code-json" width={16} />}
            onPress={onShowEstimateJson}
          >
            Show Estimate JSON
          </CtyButton>
        ) : null
      }
      onFileDrop={onFileDrop}
      onFileSelect={onFileSelect}
      onPickFile={onPickFile}
      onRemoveFile={onRemoveFile}
      onViewPdf={onViewPdf}
      fileInputRef={fileInputRef}
      footerExtra={footerExtra}
    />
  );
}

type ContractDetailsCardProps = Readonly<{
  contract: ContractData;
  rows: ReturnType<typeof contractDetailRows>;
}>;

function contractOptionSummary(c: ContractData): string {
  const holder = formatCustomerName(c.customer);
  const plan = c.coverage?.description?.trim() || '—';
  const status = c.status?.description?.trim() || '—';
  return [holder, plan, status].filter(Boolean).join(' · ');
}

type ContractPickerProps = Readonly<{
  contracts: ContractData[];
  selectedIndex: string;
  onSelectIndex: (indexStr: string) => void;
}>;

function ContractPicker({ contracts, selectedIndex, onSelectIndex }: ContractPickerProps) {
  if (contracts.length <= 1) return null;
  return (
    <div className="mt-4 p-4 border border-default-200 rounded-xl bg-content2/80">
      <p className="text-sm font-semibold text-foreground mb-1">Multiple Contracts Found</p>
      <p className="text-xs text-default-500 mb-4">Select The Contract That Applies To This Claim.</p>
      <RadioGroup
        label="Matching Contracts"
        value={selectedIndex === '' ? undefined : selectedIndex}
        onValueChange={onSelectIndex}
        classNames={{ wrapper: 'gap-3' }}
      >
        {contracts.map((c, idx) => {
          const id = String(idx);
          return (
            <Radio key={id} value={id} classNames={{ base: 'items-start max-w-full' }}>
              <div className="min-w-0 text-left">
                <p className="text-sm font-semibold text-foreground">
                  {c.contractNumber || `Contract ${idx + 1}`}
                </p>
                <p className="text-xs text-default-500 mt-0.5 break-words">{contractOptionSummary(c)}</p>
              </div>
            </Radio>
          );
        })}
      </RadioGroup>
    </div>
  );
}

function ContractDetailsCard({ contract, rows }: ContractDetailsCardProps) {
  const deductible = contract.coverage?.terms?.[0]?.deductible?.amount;

  return (
    <div className="bg-content1 rounded-xl border border-default-200 p-4">
      <div className="mb-1">
        <h3 className="text-base sm:text-lg font-semibold text-foreground">Contract Details</h3>
        <p className="text-sm text-default-500">Auto-populated from contract lookup. All fields are read-only.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-4 mt-6">
        <div>
          <p className="text-xs text-default-400 mb-0.5">{rows[0]?.label}</p>
          <p className="text-sm font-semibold text-foreground">{rows[0]?.text}</p>
        </div>
        <div>
          <p className="text-xs text-default-400 mb-0.5">Status</p>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">{contract.status?.description}</span>
            {contract.status?.code === 'A' ? (
              <Chip size="sm" color="success" variant="flat" className="text-[10px] font-bold">
                ACTIVE
              </Chip>
            ) : null}
          </div>
        </div>
        {rows.slice(1).map((row) => (
          <div key={row.key}>
            <p className="text-xs text-default-400 mb-0.5">{row.label}</p>
            <p className="text-sm font-semibold text-foreground">{row.text}</p>
          </div>
        ))}
        <div>
          <p className="text-xs text-default-400 mb-1">Deductible</p>
          <p className="text-xl font-bold text-foreground">{formatCurrency(deductible)}</p>
        </div>
        <ContractSurchargesSection contract={contract} />
      </div>
    </div>
  );
}

const NEW_CLAIM_OPTION_VALUE = 'new-claim';

function MultiVinConflictModal({
  conflict,
  onProceed,
  onStartNewClaim,
}: Readonly<{
  conflict: MultiVinConflict | null;
  onProceed: (option: MultiVinOption) => void;
  onStartNewClaim: () => void;
}>) {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState('');
  const isOpen = conflict != null;

  useEffect(() => {
    if (isOpen) setSelectedValue('');
  }, [isOpen, conflict]);

  const items = conflict?.options ?? [];

  const handleProceed = () => {
    if (selectedValue === '') return;
    if (selectedValue === NEW_CLAIM_OPTION_VALUE) {
      onStartNewClaim();
      return;
    }
    const selected = items[Number.parseInt(selectedValue, 10)];
    if (selected) onProceed(selected);
  };

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      placement="center"
      scrollBehavior="inside"
      hideCloseButton
      isDismissable={false}
      isKeyboardDismissDisabled
      classNames={{ backdrop: 'bg-black/50' }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1 border-b border-default-200">
          {conflict?.message ? (
            <span className="text-sm font-medium text-danger">{conflict.message}</span>
          ) : null}
          <span className="text-sm font-normal text-default-500">
            {t('Select the VIN you want to proceed with, or start a new claim.')}
          </span>
        </ModalHeader>
        <ModalBody className="py-4">
          <RadioGroup
            value={selectedValue === '' ? undefined : selectedValue}
            onValueChange={setSelectedValue}
            classNames={{ wrapper: 'gap-3' }}
          >
            {items.map((option, idx) => {
              const id = String(idx);
              return (
                <Radio key={`${option.vin}-${id}`} value={id} classNames={{ base: 'items-start max-w-full' }}>
                  <div className="min-w-0 text-left">
                    <p className="text-sm font-semibold text-foreground">
                      {t('VIN')}: {option.vin}
                    </p>
                    <p className="text-xs text-default-500 mt-0.5 break-words">
                      {option.fileName || option.originalFileName || t('Uploaded file')}
                    </p>
                  </div>
                </Radio>
              );
            })}
            <Radio value={NEW_CLAIM_OPTION_VALUE} classNames={{ base: 'items-start max-w-full' }}>
              <div className="min-w-0 text-left">
                <p className="text-sm font-semibold text-foreground">{t('New Claim Form')}</p>
                <p className="text-xs text-default-500 mt-0.5 break-words">
                  {t('Discard these files and start a fresh claim.')}
                </p>
              </div>
            </Radio>
          </RadioGroup>
        </ModalBody>
        <ModalFooter className="border-t border-default-200">
          <CtyButton
            color="primary"
            className="font-semibold"
            isDisabled={selectedValue === ''}
            onPress={handleProceed}
          >
            {t('Proceed')}
          </CtyButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export type ContractLookupVoiceResult =
  | { status: 'found'; contract: ContractData; contracts: ContractData[] }
  | { status: 'multiple'; contracts: ContractData[] }
  | { status: 'none'; contracts: [] }
  | { status: 'error'; message: string; contracts: [] }
  | { status: 'invalid'; message: string; contracts: [] };

type PerformLookupResult = {
  usageId: string | null;
  contracts: ContractData[];
  errorMessage?: string;
};

const VOICE_LOOKUP_EMPTY_MSG =
  'Provide a VIN, a contract number, or both before looking up the contract.';

function mapEmptyLookupError(result: PerformLookupResult): ContractLookupVoiceResult {
  const msg = result.errorMessage ?? '';
  if (!result.usageId && msg.includes('Service not ready')) {
    return { status: 'error', message: msg, contracts: [] };
  }
  if (msg.toLowerCase().includes('no contracts')) {
    return { status: 'none', contracts: [] };
  }
  if (!result.usageId) {
    return { status: 'error', message: msg, contracts: [] };
  }
  return { status: 'none', contracts: [] };
}

function mapPerformLookupToVoiceResult(result: PerformLookupResult): ContractLookupVoiceResult {
  if (result.errorMessage && result.contracts.length === 0) {
    return mapEmptyLookupError(result);
  }
  const { contracts } = result;
  if (contracts.length === 1) {
    return { status: 'found', contract: contracts[0], contracts };
  }
  if (contracts.length > 1) {
    return { status: 'multiple', contracts };
  }
  return { status: 'none', contracts: [] };
}

function resolveVoiceLookupQuery(
  built: ContractLookupQueryBuildResult,
  setVinError: (message: string) => void
): ContractLookupVoiceResult | { params: Record<string, string> } {
  if (built.vinError) {
    setVinError(built.vinError);
    return { status: 'invalid', message: built.vinError, contracts: [] };
  }
  if (built.empty || !built.params) {
    return { status: 'invalid', message: VOICE_LOOKUP_EMPTY_MSG, contracts: [] };
  }
  return { params: built.params };
}

export type ContractLookupTabHandle = {
  /** Pass overrides so voice lookup does not race React state after form_capture. */
  lookupForVoice: (overrides?: {
    vin?: string;
    contractNumber?: string;
  }) => Promise<ContractLookupVoiceResult>;
  selectContractByIndex: (index: number) => boolean;
  selectContractByNumber: (contractNumber: string) => boolean;
  getLookupCandidates: () => ContractData[];
};

type ContractLookupTabProps = Readonly<{
  onContractFound: (c: ContractData) => void;
  onLookupStart: () => void;
  vin: string;
  onVinChange: (value: string) => void;
  contractNumber: string;
  onContractNumberChange: (value: string) => void;
  contract: ContractData | null;
  session: ClaimIntakeSession | null;
  agentsBaseUrl?: string;
  token: string | null;
  lookupSourceId?: string;
  setDummyClaimId: (id: string | null) => void;
  uploadedFiles: File[];
  onUploadedFilesChange: Dispatch<SetStateAction<File[]>>;
  /** Primary document upload sources for root `sources` on the claim payload. */
  documentFileSources: AdditionalSourceItem[];
  onDocumentFileSourcesChange: Dispatch<SetStateAction<AdditionalSourceItem[]>>;
  parsedClaimDocument: Record<string, unknown> | null;
  onParsedClaimDocumentChange: (doc: Record<string, unknown> | null) => void;
  onRepairStepArtifactsClear: () => void;
  /** Clears prepared 3csmapper claim so loss codes can be re-generated after re-parse. */
  onClaimGenerationReset?: () => void;
  formSetters: ClaimIntakeParserSetters;
  /** Reports the session's effective aiworker_usage_id as soon as it is known. */
  onAiworkerUsageIdChange?: (id: string | null) => void;
  /** Existing usage log id — forwarded on subsequent estimateparser calls. */
  aiworkerUsageId?: string | null;
  onViewPdf?: (file?: File) => void;
  /** After a document upload successfully fetches VIN/contract, hide the "no estimate" checkbox for the session. */
  hideNoEstimatePrompt?: boolean;
  onHideNoEstimatePrompt?: () => void;
  /** "Are you the Servicer?" — required before file upload or VIN/contract lookup. */
  isServicer: string;
  onIsServicerChange: (value: string) => void;
  /** Opens the existing-claim supporting-documentation flow (BRD). */
  onOpenExistingClaim?: () => void;
  /** `outputs.pending_claim_numbers` from the latest VIN lookup. */
  onPendingClaimNumbersChange?: (claimNumbers: string[]) => void;
  /** Premier tenants skip the servicer question; parent already treats the answer as yes. */
  isPremierTenant?: boolean;
}>;

export const ContractLookupTab = forwardRef<ContractLookupTabHandle, ContractLookupTabProps>(
  function ContractLookupTab(
    {
      onContractFound,
      onLookupStart,
      vin,
      onVinChange,
      contractNumber,
      onContractNumberChange,
      contract,
      session,
      agentsBaseUrl,
      token,
      lookupSourceId,
      setDummyClaimId,
      uploadedFiles,
      onUploadedFilesChange,
      documentFileSources: _documentFileSources,
      onDocumentFileSourcesChange,
      parsedClaimDocument,
      onParsedClaimDocumentChange,
      onRepairStepArtifactsClear,
      onClaimGenerationReset,
      formSetters,
      onAiworkerUsageIdChange,
      aiworkerUsageId,
      onViewPdf,
      hideNoEstimatePrompt = false,
      onHideNoEstimatePrompt,
      isServicer,
      onIsServicerChange,
      onOpenExistingClaim,
      onPendingClaimNumbersChange,
      isPremierTenant = false,
    },
    ref
  ) {
  const [lookupLoading, setLookupLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lookupCandidates, setLookupCandidates] = useState<ContractData[]>([]);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadLoadingMessageIndex, setUploadLoadingMessageIndex] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadDetailError, setUploadDetailError] = useState<ClaimSubmitErrorModalContent | null>(null);
  const [lookupDetailError, setLookupDetailError] = useState<ClaimSubmitErrorModalContent | null>(null);
  const [pendingClaimNumbers, setPendingClaimNumbers] = useState<string[]>([]);
  const [multiVinConflict, setMultiVinConflict] = useState<MultiVinConflict | null>(null);
  const [isEstimateJsonModalOpen, setIsEstimateJsonModalOpen] = useState(false);
  const [noEstimateWithMe, setNoEstimateWithMe] = useState(false);
  const [revealLookupAfterUpload, setRevealLookupAfterUpload] = useState(false);
  const [notTheServicerModalOpen, setNotTheServicerModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [getAiworkersPresignedUrl] = useGetAiworkersPresignedUrlMutation();
  const [uploadToS3] = useUploadFileToS3Mutation();
  const uploadLoadingMessage = UPLOAD_LOADING_MESSAGES[uploadLoadingMessageIndex];

  const reportPendingClaimNumbers = useCallback(
    (numbers: string[]) => {
      setPendingClaimNumbers(numbers);
      onPendingClaimNumbersChange?.(numbers);
    },
    [onPendingClaimNumbersChange]
  );

  useEffect(() => {
    if (!isUploading) return undefined;
    const timer = globalThis.setInterval(() => {
      setUploadLoadingMessageIndex((prev) =>
        Math.min(prev + 1, UPLOAD_LOADING_MESSAGES.length - 1)
      );
    }, UPLOAD_MESSAGE_INTERVAL_MS);
    return () => globalThis.clearInterval(timer);
  }, [isUploading]);

  const clearUploadLoadingState = useCallback(() => {
    setIsUploading(false);
    setUploadLoadingMessageIndex(0);
  }, []);

  const applyClaimIntakeFailure = useCallback(
    (
      err: unknown,
      options: Readonly<{ heading: string; fallback: string; inlineTarget: 'upload' | 'lookup' }>
    ) => {
      const presentation = presentClaimIntakeError(err, {
        heading: options.heading,
        fallback: options.fallback,
        preserveDetails: options.inlineTarget === 'lookup',
      });
      if (presentation.kind === 'modal') {
        if (options.inlineTarget === 'upload') {
          setUploadDetailError(presentation.modal);
          setUploadError(null);
        } else {
          setLookupDetailError(presentation.modal);
          setError(null);
        }
      } else if (options.inlineTarget === 'upload') {
        setUploadError(presentation.message);
        setUploadDetailError(null);
      } else {
        setLookupDetailError(toLookupDetailError(presentation.message));
        setError(null);
      }
      CtyShowToast('error', presentation.kind === 'modal' ? presentation.modal.summary : presentation.message);
    },
    []
  );

  const handleSelectContractCandidate = useCallback(
    (indexStr: string) => {
      setSelectedCandidateIndex(indexStr);
      const idx = Number.parseInt(indexStr, 10);
      const chosen = lookupCandidates[idx];
      if (chosen) onContractFound(chosen);
    },
    [lookupCandidates, onContractFound]
  );

  useEffect(() => {
    if (!contract || lookupCandidates.length <= 1) return;
    const idx = lookupCandidates.findIndex((c) => c.contractNumber === contract.contractNumber);
    if (idx >= 0) setSelectedCandidateIndex(String(idx));
  }, [contract, lookupCandidates]);

  /**
   * Runs the VIN contract lookup.
   * `incomingUsageId` — when provided (PDF path), it is forwarded to the VIN API call so the
   * backend links it to the same log as estimateparser. In that case it is also returned as-is
   * so downstream callers keep using the estimateparser id.
   * When absent (manual VIN path), the VIN response's own aiworker_usage_id is used and returned.
   */
  const performLookup = useCallback(
    async (
      queryParameters: Record<string, string>,
      incomingUsageId?: string | null
    ): Promise<{
      usageId: string | null;
      contracts: ContractData[];
      errorMessage?: string;
    }> => {
      if (!token?.trim() || !session) {
        setError('Service not ready yet — please try again in a moment.');
        return {
          usageId: null,
          contracts: [],
          errorMessage: 'Service not ready yet — please try again in a moment.',
        };
      }
      setError(null);
      setLookupDetailError(null);
      setLookupCandidates([]);
      setSelectedCandidateIndex('');
      setLookupLoading(true);
      setDummyClaimId(null);
      reportPendingClaimNumbers([]);
      onLookupStart();

      try {
        const { data: result, aiworkerUsageId: vinUsageId, pendingClaimNumbers: lookupPendingClaimNumbers } =
          await runClaimIntakeOperation({
            session,
            event: 'VIN',
            userInput: {
              source_id: lookupSourceId,
              payload: { query_parameters: queryParameters },
            },
            token,
            baseUrl: agentsBaseUrl,
            ...(incomingUsageId ? { aiworkerUsageId: incomingUsageId } : {}),
          });

        const effectiveId = (incomingUsageId?.trim() || vinUsageId?.trim() || null);
        if (effectiveId) onAiworkerUsageIdChange?.(effectiveId);

        const extractedContracts = extractContractsFromLookupResult(result);
        const contracts =
          extractedContracts?.filter((c) => c.coverage?.productType?.code !== 'GAP') ?? [];

        if (!contracts.length) {
          setError('No contracts found for the provided lookup details.');
          reportPendingClaimNumbers([]);
          return {
            usageId: effectiveId,
            contracts: [],
            errorMessage: 'No contracts found for the provided lookup details.',
          };
        }

        if (contracts.length === 1) {
          setLookupCandidates([]);
          setSelectedCandidateIndex('');
          onContractFound(contracts[0]);
        } else {
          setLookupCandidates(contracts);
          setSelectedCandidateIndex('');
        }
        reportPendingClaimNumbers(lookupPendingClaimNumbers);
        return { usageId: effectiveId, contracts };
      } catch (err: unknown) {
        setLookupCandidates([]);
        setSelectedCandidateIndex('');
        applyClaimIntakeFailure(err, {
          heading: 'Contract lookup failed',
          fallback: 'An error occurred while looking up the contract.',
          inlineTarget: 'lookup',
        });
        return {
          usageId: null,
          contracts: [],
          errorMessage: 'An error occurred while looking up the contract.',
        };
      } finally {
        onHideNoEstimatePrompt?.();
        setLookupLoading(false);
      }
    },
    [
      token,
      session,
      agentsBaseUrl,
      onContractFound,
      onLookupStart,
      setDummyClaimId,
      lookupSourceId,
      onAiworkerUsageIdChange,
      applyClaimIntakeFailure,
      onHideNoEstimatePrompt,
      reportPendingClaimNumbers,
    ]
  );

  useImperativeHandle(
    ref,
    () => ({
      lookupForVoice: async (overrides) => {
        if (isServicer !== 'yes') {
          return {
            status: 'invalid' as const,
            message: 'Confirm you are the servicer before looking up a contract.',
            contracts: [],
          };
        }
        const effectiveVin = (overrides?.vin ?? vin).trim();
        const effectiveContractNumber = (overrides?.contractNumber ?? contractNumber).trim();
        // Keep the visible inputs in sync when voice supplies overrides before React state catches up.
        if (overrides?.vin != null && overrides.vin !== vin) {
          onVinChange(sanitizeVinInput(overrides.vin));
        }
        if (overrides?.contractNumber != null && overrides.contractNumber !== contractNumber) {
          onContractNumberChange(overrides.contractNumber);
        }
        const built = buildContractLookupQueryParameters(effectiveVin, effectiveContractNumber);
        const resolved = resolveVoiceLookupQuery(built, (message) => {
          setLookupDetailError(toLookupDetailError(message));
        });
        if ('status' in resolved) return resolved;
        setLookupDetailError(null);
        return mapPerformLookupToVoiceResult(
          await performLookup(resolved.params, aiworkerUsageId?.trim() || null)
        );
      },
      selectContractByIndex: (index: number) => {
        const chosen = lookupCandidates[index];
        if (!chosen) return false;
        setSelectedCandidateIndex(String(index));
        onContractFound(chosen);
        return true;
      },
      selectContractByNumber: (contractNo: string) => {
        const needle = contractNo.trim().toLowerCase();
        if (!needle) return false;
        const idx = lookupCandidates.findIndex(
          (c) => (c.contractNumber ?? '').trim().toLowerCase() === needle
        );
        if (idx < 0) return false;
        setSelectedCandidateIndex(String(idx));
        onContractFound(lookupCandidates[idx]);
        return true;
      },
      getLookupCandidates: () => lookupCandidates,
    }),
    [vin, contractNumber, performLookup, lookupCandidates, onContractFound, onVinChange, onContractNumberChange, aiworkerUsageId, isServicer]
  );

  const handleVinChange = useCallback(
    (value: string) => {
      const sanitized = sanitizeVinInput(value);
      onVinChange(sanitized);
      setLookupDetailError(null);
      if (!sanitized) {
        setError(null);
        setLookupCandidates([]);
        setSelectedCandidateIndex('');
        reportPendingClaimNumbers([]);
        onLookupStart();
      }
    },
    [onVinChange, onLookupStart, reportPendingClaimNumbers]
  );

  const handleContractNumberChange = useCallback(
    (value: string) => {
      onContractNumberChange(value);
      if (!value.trim()) {
        setError(null);
        setLookupCandidates([]);
        setSelectedCandidateIndex('');
        reportPendingClaimNumbers([]);
        onLookupStart();
      }
    },
    [onContractNumberChange, onLookupStart, reportPendingClaimNumbers]
  );

  const handleIsServicerChange = useCallback(
    (value: string) => {
      onIsServicerChange(value);
      if (value === 'no') {
        setNotTheServicerModalOpen(true);
        return;
      }
      setNotTheServicerModalOpen(false);
    },
    [onIsServicerChange]
  );

  const handleLookup = useCallback(() => {
    if (isUploading || isServicer !== 'yes') return;
    const built = buildContractLookupQueryParameters(vin, contractNumber);
    if (built.empty) return;
    if (built.vinError) {
      setLookupDetailError(toLookupDetailError(built.vinError));
      return;
    }
    if (!built.params) return;
    setLookupDetailError(null);
    void performLookup(built.params, aiworkerUsageId?.trim() || null);
  }, [performLookup, vin, contractNumber, aiworkerUsageId, isUploading, isServicer]);

  const canLookup = Boolean(vin.trim() || contractNumber.trim());

  /**
   * Pushes parser_results into the step-3 form, auto-fills VIN, triggers VIN lookup if VIN present.
   * `incomingUsageId` (from estimateparser) is forwarded to the VIN call so all three calls share the same log.
   * Returns the effective aiworker_usage_id to use for downstream calls (3csmapper, saveclaim).
   */
  const applyParsedResultsAndMaybeLookup = useCallback(
    async (parserResults: Record<string, unknown>, incomingUsageId?: string | null): Promise<string | null> => {
      applyParserResultsToFormState(parserResults, formSetters);
      const extractedVin = extractVinFromParserResults(parserResults);
      const extractedContractNumber = extractContractNumberFromParserResults(parserResults);
      setRevealLookupAfterUpload(true);
      if (extractedVin) {
        const sanitized = sanitizeVinInput(extractedVin);
        onVinChange(sanitized);
        setLookupDetailError(null);
        CtyShowToast('success', 'Document parsed — VIN detected, looking up contract.');
        const built = buildContractLookupQueryParameters(sanitized, extractedContractNumber || contractNumber, {
          skipVinValidation: true,
        });
        if (built.params) {
          const lookup = await performLookup(built.params, incomingUsageId);
          if (lookup.contracts.length > 0) onHideNoEstimatePrompt?.();
          return lookup.usageId;
        }
        return incomingUsageId ?? null;
      }
      if (extractedContractNumber) {
        onContractNumberChange(extractedContractNumber);
        CtyShowToast('success', 'Document parsed — contract number detected, looking up contract.');
        const built = buildContractLookupQueryParameters('', extractedContractNumber);
        if (built.params) {
          const lookup = await performLookup(built.params, incomingUsageId);
          if (lookup.contracts.length > 0) onHideNoEstimatePrompt?.();
          return lookup.usageId;
        }
        return incomingUsageId ?? null;
      }
      CtyShowToast('success', 'Document parsed — please enter the VIN or contract number to continue.');
      return incomingUsageId ?? null;
    },
    [formSetters, onVinChange, onContractNumberChange, performLookup, contractNumber, onHideNoEstimatePrompt]
  );

  /**
   * Uploads new files to S3, runs estimateparser for each batch, applies parser fields,
   * and auto-triggers VIN lookup when the parser returns a VIN or contract number.
   * Reuses `aiworkerUsageId` when already set so all events stay on one usage log.
   */
  const processFilesUpload = useCallback(
    async (incomingFiles: File[]) => {
      const newFiles = incomingFiles.filter(
        (file) => !uploadedFiles.some((existing) => isSameUploadedFile(existing, file))
      );
      if (newFiles.length === 0) return;

      setIsUploading(true);
      setUploadLoadingMessageIndex(0);
      setUploadError(null);
      setUploadDetailError(null);

      const isFirstParse = !parsedClaimDocument;
      if (isFirstParse) {
        onParsedClaimDocumentChange(null);
        onRepairStepArtifactsClear();
      }

      try {
        const { urlList } = await uploadClaimIntakeFilesViaPresigned({
          files: newFiles,
          purpose: 'estimate_parser',
          sessionId: session?.sessionId,
          aiworkerUsageId: aiworkerUsageId,
          getPresignedUrls: (payload) => getAiworkersPresignedUrl(payload).unwrap(),
          uploadOne: (file, presignedUrl) =>
            uploadToS3({ file, presignedUrl, CtyShowToast }).unwrap(),
        });

        onUploadedFilesChange((prev) => [...prev, ...newFiles]);

        const sources = buildEstimateParserSources(newFiles, urlList);
        if (sources.length === 0) {
          clearUploadLoadingState();
          return;
        }

        const newFileSources = buildAdditionalSourcesFromGenerateResponse(urlList, newFiles);
        if (newFileSources.length > 0) {
          onDocumentFileSourcesChange((prev) => [...prev, ...newFileSources]);
        }

        await new Promise((resolve) => setTimeout(resolve, 1500));

        if (!session) {
          throw new Error('Service not ready yet — please try again in a moment.');
        }

        const chainedUsageId = aiworkerUsageId?.trim() || undefined;
        const { data: parserData, aiworkerUsageId: estimateParserUsageId } = await runClaimIntakeOperation({
          session,
          event: 'estimateparser',
          userInput: {
            sources,
            token,
          },
          token: token ?? '',
          baseUrl: agentsBaseUrl,
          ...(chainedUsageId ? { aiworkerUsageId: chainedUsageId } : {}),
        });

        const effectiveUsageId = (chainedUsageId ?? estimateParserUsageId?.trim()) || null;
        if (effectiveUsageId) onAiworkerUsageIdChange?.(effectiveUsageId);

        const { docItem, parserResults } = extractParsedDocumentAndResults(parserData);
        if (docItem) onParsedClaimDocumentChange(docItem);

        clearUploadLoadingState();

        if (parserResults) {
          onClaimGenerationReset?.();
          void applyParsedResultsAndMaybeLookup(parserResults, effectiveUsageId);
        }
      } catch (err: unknown) {
        const vinConflict = extractMultiVinConflict(err);
        if (vinConflict) {
          clearUploadLoadingState();
          setMultiVinConflict(vinConflict);
          return;
        }
        applyClaimIntakeFailure(err, {
          heading: 'Document processing failed',
          fallback: 'Upload failed.',
          inlineTarget: 'upload',
        });
        clearUploadLoadingState();
      }
    },
    [
      uploadedFiles,
      parsedClaimDocument,
      aiworkerUsageId,
      getAiworkersPresignedUrl,
      uploadToS3,
      token,
      session,
      agentsBaseUrl,
      onUploadedFilesChange,
      onDocumentFileSourcesChange,
      onParsedClaimDocumentChange,
      onRepairStepArtifactsClear,
      onClaimGenerationReset,
      clearUploadLoadingState,
      applyParsedResultsAndMaybeLookup,
      onAiworkerUsageIdChange,
      applyClaimIntakeFailure,
    ]
  );

  const handleFilesChosen = useCallback(
    (files: File[]) => {
      if (lookupLoading || isUploading || isServicer !== 'yes') return;
      if (files.length > 0) {
        void processFilesUpload(files);
      }
    },
    [processFilesUpload, lookupLoading, isUploading, isServicer]
  );

  const handleFileDrop = useCallback(
    (e: React.DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length > 0) handleFilesChosen(files);
    },
    [handleFilesChosen]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length > 0) handleFilesChosen(files);
      if (e.target) e.target.value = '';
    },
    [handleFilesChosen]
  );

  const removeFile = useCallback(
    (index: number) => {
      const file = uploadedFiles[index];
      const parsedName = getParsedDocumentFileName(parsedClaimDocument);
      const isParsedFile = Boolean(file && parsedName && file.name === parsedName);

      onUploadedFilesChange((prev) => prev.filter((_, i) => i !== index));
      if (file) {
        onDocumentFileSourcesChange((prev) => removeAdditionalSourcesForFile(prev, file));
      }

      if (isParsedFile) {
        onParsedClaimDocumentChange(null);
        onRepairStepArtifactsClear();
      }
    },
    [
      uploadedFiles,
      parsedClaimDocument,
      onUploadedFilesChange,
      onDocumentFileSourcesChange,
      onParsedClaimDocumentChange,
      onRepairStepArtifactsClear,
    ]
  );

  /**
   * Applies the VIN the user chose from the multi-VIN conflict modal: keeps only the selected file
   * in the UI, stores its parsed document, then runs the usual form-fill + VIN contract lookup.
   */
  const handleProceedWithSelectedVin = useCallback(
    (option: MultiVinOption) => {
      setMultiVinConflict(null);
      onUploadedFilesChange((prev) => {
        const kept = prev.filter(
          (file) => file.name === option.fileName || file.name === option.originalFileName
        );
        return kept.length > 0 ? kept : prev;
      });
      onDocumentFileSourcesChange((prev) => {
        const kept = prev.filter(
          (source) =>
            source.file_name === option.fileName || source.file_name === option.originalFileName
        );
        return kept.length > 0 ? kept : prev;
      });
      onParsedClaimDocumentChange(option.docItem);
      onClaimGenerationReset?.();
      if (option.parserResults) {
        void applyParsedResultsAndMaybeLookup(option.parserResults, aiworkerUsageId?.trim() || null);
      }
    },
    [
      onUploadedFilesChange,
      onDocumentFileSourcesChange,
      onParsedClaimDocumentChange,
      onClaimGenerationReset,
      applyParsedResultsAndMaybeLookup,
      aiworkerUsageId,
    ]
  );

  const rows = contract ? contractDetailRows(contract) : [];
  const pickFile = useCallback(() => {
    if (lookupLoading || isUploading || isServicer !== 'yes') return;
    fileInputRef.current?.click();
  }, [lookupLoading, isUploading, isServicer]);
  const isServicerConfirmed = isServicer === 'yes';
  const hideIntakeUi = isServicer === 'no';
  const lookupInputsDisabled = isUploading || !isServicerConfirmed;
  const uploadDisabled = lookupLoading || !isServicerConfirmed;
  const showLookupSection = hideNoEstimatePrompt || noEstimateWithMe || revealLookupAfterUpload;

  return (
    <div className="space-y-4 sm:space-y-6">
      <VehicleNotAtCenterModal
        isOpen={notTheServicerModalOpen}
        onClose={() => setNotTheServicerModalOpen(false)}
        onStartNewClaim={() => globalThis.location.reload()}
        title="Servicer Required"
        message={NOT_THE_SERVICER_DISCLAIMER}
      />

      {isPremierTenant ? null : (
      <div className="bg-content1 rounded-xl border border-default-200 p-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
          <h2 className="text-base sm:text-lg font-semibold text-foreground">Servicer Confirmation</h2>
          <Chip size="sm" color="danger" variant="flat" className="uppercase text-[10px] font-bold px-2">
            Required
          </Chip>
        </div>
        <p className="text-sm text-default-500 mb-5">
          Confirm you are the repair facility submitting this claim before uploading documents or looking up a contract.
        </p>
        <LabeledHorizontalRadios
          id="are-you-the-servicer"
          label="Are you the Servicer?"
          value={isServicer}
          onValueChange={handleIsServicerChange}
          options={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
        />
      </div>
      )}

      {hideIntakeUi ? null : (
      <>
      <DocumentUploadCard
        isUploading={isUploading}
        isDisabled={uploadDisabled}
        uploadLoadingMessage={uploadLoadingMessage}
        uploadError={uploadError}
        uploadDetailError={uploadDetailError}
        uploadedFiles={uploadedFiles}
        parsedClaimDocument={parsedClaimDocument}
        onShowEstimateJson={() => setIsEstimateJsonModalOpen(true)}
        onFileDrop={handleFileDrop}
        onFileSelect={handleFileSelect}
        onPickFile={pickFile}
        onRemoveFile={removeFile}
        onViewPdf={onViewPdf}
        fileInputRef={fileInputRef}
        footerExtra={
          hideNoEstimatePrompt ? null : (
            <Checkbox
              className="mt-3"
              isSelected={noEstimateWithMe}
              onValueChange={setNoEstimateWithMe}
              isDisabled={isUploading || lookupLoading || !isServicerConfirmed}
            >
              I don&apos;t have estimate with me
            </Checkbox>
          )
        }
      />

      {showLookupSection ? (
      <div className="bg-content1 rounded-xl border border-default-200 p-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
          <h2 className="text-base sm:text-lg font-semibold text-foreground">Contract Lookup</h2>
          <Chip size="sm" color="danger" variant="flat" className="uppercase text-[10px] font-bold px-2">
            Required
          </Chip>
        </div>
        <p className="text-sm text-default-500 mb-5">
          {'Enter a VIN, contract number, or both to search for an existing warranty contract.'}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
          <Input
            label={VIN_INPUT_LABEL}
            value={vin}
            onValueChange={handleVinChange}
            maxLength={17}
            variant="bordered"
            size="lg"
            isDisabled={lookupInputsDisabled}
            classNames={{ inputWrapper: 'border-default-300' }}
            description="Enter the last 8 digits or the full 17-character alphanumeric VIN."
            endContent={
              <span className="flex w-6 shrink-0 items-center justify-end">
                { vin && !lookupInputsDisabled ? (
                  <button type="button" onClick={() => handleVinChange('')} className="text-default-400 hover:text-default-500">
                    <Icon icon="mdi:close-circle" width={18} />
                  </button>
                ) : null}
              </span>
            }
          />
          <Input
            label="Contract Number"
            value={contractNumber}
            onValueChange={handleContractNumberChange}
            variant="bordered"
            size="lg"
            isDisabled={lookupInputsDisabled}
            classNames={{ inputWrapper: 'border-default-300' }}
            endContent={
              contractNumber && !lookupInputsDisabled ? (
                <button
                  type="button"
                  onClick={() => handleContractNumberChange('')}
                  className="text-default-400 hover:text-default-500"
                >
                  <Icon icon="mdi:close-circle" width={18} />
                </button>
              ) : null
            }
          />
        </div>
        {lookupDetailError ? (
          <div className="mt-4">
            <ClaimIntakeDetailErrors content={lookupDetailError} />
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <CtyButton
            color="primary"
            isLoading={lookupLoading}
            onPress={handleLookup}
            isDisabled={!canLookup || lookupInputsDisabled}
            startContent={!lookupLoading && <Icon icon="mdi:magnify" width={18} />}
            className="font-semibold"
          >
            Lookup Contract
          </CtyButton>

          {contract?.contractNumber ? (
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
              <Icon icon="mdi:check-circle" width={18} />
              <span>Contract retrieved — {contract.contractNumber}</span>
            </div>
          ) : null}
        </div>
        {hasBlockingPendingClaims(pendingClaimNumbers) ? (
          <div
            className="mt-4 p-3 rounded-lg border border-danger bg-danger/10 text-sm text-danger"
            role="alert"
          >
            {pendingClaimBlockMessage(pendingClaimNumbers.length)}
          </div>
        ) : null}

        {error ? (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/40 rounded-lg text-sm text-red-600 dark:text-red-400">{error}</div>
        ) : null}

        <ContractPicker
          contracts={lookupCandidates}
          selectedIndex={selectedCandidateIndex}
          onSelectIndex={handleSelectContractCandidate}
        />
      </div>
      ) : null}

      {contract ? <ContractDetailsCard contract={contract} rows={rows} /> : null}

      {onOpenExistingClaim && !contract ? (
        <div className="bg-content1 rounded-xl border border-default-200 p-4">
          <h2 className="text-base sm:text-lg font-semibold text-foreground mb-1">
            Submit Information on an Existing Claim Here
          </h2>
          <p className="text-sm text-default-500 mb-4">
            Already started a claim? Access it to upload supporting documents below.
          </p>
          <CtyButton
            color="primary"
            variant="flat"
            className="font-semibold"
            onPress={onOpenExistingClaim}
            isDisabled={isUploading || lookupLoading}
            endContent={<Icon icon="mdi:arrow-right" width={18} />}
          >
            Access Existing Claim
          </CtyButton>
        </div>
      ) : null}
      </>
      )}

      <MultiVinConflictModal
        conflict={multiVinConflict}
        onProceed={handleProceedWithSelectedVin}
        onStartNewClaim={() => globalThis.location.reload()}
      />

      <EstimateParserJsonModal
        isOpen={isEstimateJsonModalOpen}
        onClose={() => setIsEstimateJsonModalOpen(false)}
        parsedClaimDocument={parsedClaimDocument}
      />
    </div>
  );
  }
);
